import { useState, useRef, useEffect } from 'react';
import { Skeleton, Spinner } from '@chakra-ui/react';
import { ArrowDownIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { classNames, convertPlus53To5, extractNauta, extractNumbers } from '../../utils/common';
import PropTypes, { shape } from 'prop-types';

/* Config and utils */
const types = {
  movil: {},
  email: {},
};

const placeholder = {
  movil: '5XXXXXXX',
  email: 'mi.contacto@nauta.cu',
};

/* Elements */
const truncateLabel = (text, max = 8) =>
  text && text.length > max ? text.slice(0, max) + '…' : text;

const BoxOption = ({ icon, option, onClick, isAdding }) => {
  const label = truncateLabel(option?.name || option.value);

  return (
    <div
      onClick={onClick}
      className={classNames(
        'relative flex w-fit max-w-[140px] items-center justify-between px-3 py-1 space-x-2 bg-gray-200 text-gray-600 text-[13px] select-none whitespace-nowrap rounded-full pr-8',
        onClick !== undefined &&
          !isAdding &&
          'hover:bg-gray-300 hover:shadow-lg active:scale-[0.97] cursor-pointer',
      )}
    >
      {icon}
      <span
        className={classNames(
          'flex items-center',
          isAdding ? 'opacity-40 text-primary' : '',
        )}
      >
        {label}
      </span>
      {isAdding && (
        <div className="absolute inset-0 -left-2">
          <Skeleton w={'100%'} h={'100%'} />
        </div>
      )}
    </div>
  );
};

BoxOption.propTypes = {
  icon: PropTypes.element,
  option: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string.isRequired,
  }).isRequired,
  onClick: PropTypes.func,
  isAdding: PropTypes.bool,
};

const Option = ({
  option,
  inputValue,
  isSelected,
  onClick,
  isAdding,
  type,
}) => {
  const value =
    type === 'movil' ? option?.value?.replace(/^\+53/, '') : option?.value;

  const highlightedName = option?.name?.replace(
    new RegExp(`(${inputValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
    `<strong class="${
      isAdding ? 'text-red-error' : 'text-gray-700'
    }">$1</strong>`,
  );
  const highlightedValue = value.replace(
    new RegExp(`(${inputValue.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'),
    `<strong class="${
      isAdding ? 'text-red-error' : 'text-gray-700'
    }">$1</strong>`,
  );

  return (
    <div
      onClick={onClick}
      className={classNames(
        (option?.name === '' || option?.value === '') && 'py-3',
        'relative px-4 py-1 select-none whitespace-nowrap text-start',
        isAdding
          ? 'text-red-error cursor-not-allowed'
          : isSelected
          ? 'bg-primary text-gray-100 font-semibold hover:bg-primary/80 cursor-pointer'
          : 'bg-white hover:bg-gray-100 text-gray-600 cursor-pointer',
      )}
    >
      <div
        className="text-lg truncate"
        dangerouslySetInnerHTML={{ __html: highlightedName }}
      />
      <div
        className="text-xs truncate"
        dangerouslySetInnerHTML={{ __html: highlightedValue }}
      />

      {isSelected && (
        <span
          className={classNames(
            'text-green-secondary',
            'absolute inset-y-0 right-0 flex items-center pr-4',
          )}
        >
          <CheckIcon className="h-5 w-5" aria-hidden="true" />
        </span>
      )}

      {isAdding && (
        <>
          <span className="ml-3 italic font-extralight">(agregando...)</span>
          <div className="absolute inset-0">
            <Skeleton className="opacity-75" w={'100%'} h={'100%'} />
          </div>
        </>
      )}
    </div>
  );
};

Option.propTypes = {
  option: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string.isRequired,
  }).isRequired,
  inputValue: PropTypes.string.isRequired,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  isAdding: PropTypes.bool,
  type: PropTypes.oneOf(Object.keys(types)),
};

const Multiselect = ({
  type,
  listOptions = [],
  initialSelected,
  isLoading = false,
  onChange,
  reset,
  icon,
}) => {
  /* STATES */
  const [options, setOptions] = useState(
    listOptions.map((option) => ({
      name: option.name,
      value: type === 'movil' ? convertPlus53To5(option).value : option.value,
      email: option.email,
      phone: convertPlus53To5(option).value,
    }))
      // remove duplicated
      .filter((option, i, arr) => arr.indexOf(option) === i) || [],
  );
  const [selectedOptions, setSelectedOptions] = useState(initialSelected || []);
  const [inputValue, setInputValue] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [withPhoneCode, setWithPhoneCode] = useState(false);

  const optionsContainerRef = useRef(null);
  const inputRef = useRef(null);
  const arrowRef = useRef(null);
  const boxOptionsRef = useRef(null);
  const moreRef = useRef(null);

  /* EFFECTS */
  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  useEffect(() => {
    setOptions(listOptions.map((option) => ({
      name: option.name,
      value: type === 'movil' ? convertPlus53To5(option).value : option.value,
      email: option.email,
      phone: convertPlus53To5(option).value,
    }))
      // remove duplicated
      .filter((option, i, arr) => arr.indexOf(option) === i) || []);

    if (listOptions.length === 0) setSelectedOptions([]);
  }, [listOptions]);

  useEffect(() => {
    if (reset?.value) {
      setSelectedOptions([]);
      setInputValue('');
      setShowOptions(false);

      if (reset.setValue) reset.setValue(false);
    }
  }, [reset]);

  /* FUNCTIONS */
  const isSelected = (option) => {
    return selectedOptions.some((selectedOption) => {
      if (type === 'movil') return convertPlus53To5(selectedOption).value === convertPlus53To5(option).value;
      return selectedOption.value === option.value;
    });
  }

  const isListed = (option) => {
    return options.some((listedOption) => {
      if (type === 'movil') return convertPlus53To5(listedOption).value === convertPlus53To5(option).value;
      return listedOption.value === option.value;
    });
  }

  const handleInputChange = (event) => {
    const value = String(event.target.value);

    if (type === 'movil') {
      setWithPhoneCode(/^\+\d*$/.test(value) || '');

      const numbExtracted = extractNumbers(value.trim());

      if (Array.isArray(numbExtracted)) {
        let optionsToAddList = [];
        let optionsToAddSelected = [];
        numbExtracted.forEach((numb) => {
          if (!isListed({ value: numb })) {
            optionsToAddList.push({
              name: '',
              value: convertPlus53To5({value: numb}).value,
            });
          }
          if (!isSelected({ value: numb })){
            optionsToAddSelected.push({
              name: '',
              value: convertPlus53To5({value: numb}).value,
            });
          }
        });
        if (onChange !== undefined)
          onChange([...selectedOptions, ...optionsToAddSelected]);
        setOptions( prev => {
          const newOptions = [...prev, ...optionsToAddList]
            .filter((option, i, arr) => arr.indexOf(option) === i);
          return newOptions;
        });
        setSelectedOptions([...selectedOptions, ...optionsToAddSelected]);

        setInputValue('');
        if (type === 'movil') setWithPhoneCode(false);
        return setShowOptions(false);
      }
      return setInputValue(numbExtracted);
    }

    // if its a email
    const nautaExtracted = extractNauta(value);

    if (Array.isArray(nautaExtracted)) {
      let optionsToAdd = [];
      nautaExtracted.forEach((nauta) => {
        optionsToAdd.push({
          name: '',
          value: nauta,
        });
      });
      if (onChange !== undefined)
        onChange([...selectedOptions, ...optionsToAdd]);
      setOptions(
        [...options, ...optionsToAdd]
        // remove duplicated
        .filter((option, i, arr) => arr.indexOf(option) === i) || [],
      );
      setSelectedOptions([...selectedOptions, ...optionsToAdd]);

      setInputValue('');
      return setShowOptions(false);
    }
    return setInputValue(nautaExtracted);
  };

  const handleOptionClick = (option) => {
    let optionsToSet = [...selectedOptions];
    if (isSelected(option)) {
      optionsToSet = selectedOptions.filter(
        (selectedOption) => convertPlus53To5(selectedOption).value !== convertPlus53To5(option).value,
      );
    } else {
      // hacer un substring de option para que no se guarde el +53 en caso de que lo tenga
      optionsToSet = [
        ...selectedOptions,
        type === 'movil' ? convertPlus53To5(option) : option,
      ]
      // remove duplicates
        .filter((option, i, arr) => arr.indexOf(option) === i)
      ;
    }
    setSelectedOptions(optionsToSet);
    setInputValue('');
    setShowOptions(false);

    if (onChange !== undefined) onChange(optionsToSet);
  };

  const handleDocumentClick = (event) => {
    if (
      !optionsContainerRef.current?.contains(event.target) &&
      !inputRef.current?.contains(event.target) &&
      !arrowRef.current?.contains(event.target) &&
      !boxOptionsRef.current?.contains(event.target) &&
      !moreRef.current?.contains(event.target)
    ) {
      setShowOptions(false); // Ocultar contenedor de opciones al hacer clic fuera de él
    }
  };

  return (
    <div>
      <div className="relative flex items-center group select-none ring-1 rounded-full focus:border-blue-500 border-0 ring-blue-300 bg-white px-1">
        {/* Icon / Clear all button */}
        {icon && (
          <div
            className={classNames(
              'flex items-center justify-center pl-3 pr-2',
              selectedOptions.length > 0 && 'cursor-pointer',
            )}
            onClick={() => {
              if (selectedOptions.length > 0) {
                setSelectedOptions([]);
                setInputValue('');
                setShowOptions(false);
                if (onChange !== undefined) onChange([]);
              }
            }}
          >
            {selectedOptions.length > 0 ? (
              <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" />
            ) : (
              icon
            )}
          </div>
        )}

        {/* Switch phone code by user entry */}
        {type === 'movil' && !withPhoneCode && (
          <div className="text-gray-400 sm:text-sm h-full flex items-center pl-1">
            +53
          </div>
        )}

        {/* Input */}
        <input
          type="text"
          placeholder={placeholder[type]}
          value={inputValue}
          onChange={handleInputChange}
          ref={inputRef}
          //disabled={isLoading}
          className={classNames(
            //isLoading && 'opacity-50',
            'w-full py-1.5 pl-1 ring-0 rounded-full pr-6 border-0 text-gray-900 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:ring-0',
            !isLoading && 'group-hover:border-gray-400',
          )}
          onFocus={() => setShowOptions(true)}
        />

        {/* Options */}
        {(inputValue !== '' || showOptions) && (
          <div
            className={classNames(
              options.filter(
                (option) =>
                  option?.name
                    ?.toLowerCase()
                    ?.includes(inputValue.toLowerCase() ) ||
                  option.value.toLowerCase().includes(inputValue.toLowerCase()),
              ).length === 0 && 'hidden',
              'absolute top-11 inset-x-0 w-full max-h-[280px] bg-white border-[1px] border-gray-300 rounded-md shadow-md z-50 overflow-y-auto divide-y-[1px] divide-gray-100',
            )}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                inputRef.current.focus();
              }
            }}
            tabIndex={0}
            ref={optionsContainerRef}
            onBlur={() => {
              setShowOptions(false);
            }}
          >
            {inputValue !== ''
              ? options
                  .filter(
                    (option) =>
                      option?.name
                        ?.toLowerCase()
                        ?.includes(inputValue.toLowerCase()) ||
                      option.value
                        .toLowerCase()
                        .includes(inputValue.toLowerCase()),
                  )
                  .sort((a, b) => {
                    if (isSelected(a) && !isSelected(b)) return -1;
                    if (!isSelected(a) && isSelected(b)) return 1;
                    return 0;
                  })
                  .filter((v,i,a)=>a.findIndex(t=>(t.value===v.value))===i)
                  .map((option, index) => (
                    <Option
                      key={index + 'optionMultiselect'}
                      option={option}
                      inputValue={inputValue}
                      isSelected={isSelected(option)}
                      onClick={() => {
                        handleOptionClick(option);
                      }}
                      isAdding={false}
                      type={type}
                    />
                  ))
              : options
                .sort((a, b) => {
                  if (isSelected(a) && !isSelected(b)) return -1;
                  if (!isSelected(a) && isSelected(b)) return 1;
                  return 0;
                })
                .filter((v,i,a)=>a.findIndex(t=>(t.value===v.value))===i)
                .map((option, index) => (
                  <Option
                    key={index + 'optionMultiselect'}
                    option={option}
                    inputValue={inputValue}
                    isSelected={isSelected(option)}
                    onClick={() => {
                      handleOptionClick(option);
                    }}
                    isAdding={false}
                    type={type}
                  />
                ))}
          </div>
        )}

        {/* Selected options */}
        {selectedOptions.length > 0 && (
          <div
            ref={boxOptionsRef}
            className="h-full flex items-center flex-row-reverse gap-x-2 bg-gray-100 rounded-full cursor-pointer"
            onClick={() => {
              if (isLoading) return;
              setShowOptions((prev) => !prev);
            }}
          >
            {selectedOptions.length > 1 && (
              <span className="relative flex w-fit items-center justify-between px-3 py-1 space-x-2 bg-gray-200 text-gray-600 text-[13px] select-none whitespace-nowrap rounded-full pr-8">
                {selectedOptions.length} seleccionados
              </span>
            )}
            {selectedOptions.length === 1 && (
              <BoxOption
                option={selectedOptions[0]}
                icon={null}
                isAdding={false}
              />
            )}
          </div>
        )}

        {/* Arrow down */}
        {options.length > 0 && !isLoading && (
          <div
            ref={arrowRef}
            className="absolute right-0 top-0 pr-2.5 h-full flex items-center flex-row-reverse gap-x-2"
          >
            <ArrowDownIcon
              className={classNames(
                isLoading && 'opacity-50',
                'flex items-center w-4 h-4 text-gray-400',
                !isLoading &&
                  'group-hover:text-gray-700 group-[input]-active:text-primary',
              )}
              onClick={() => {
                if (isLoading) return;
                setShowOptions((prev) => !prev);
              }}
            />
          </div>
        )}

        {isLoading && (
          <Spinner className="absolute right-1.5 inset-y-1.5 opacity-10" />
        )}
      </div>
    </div>
  );
};

Multiselect.propTypes = {
  type: PropTypes.oneOf(Object.keys(types)),
  listOptions: PropTypes.arrayOf(
    shape({
      name: PropTypes.string,
      value: PropTypes.string.isRequired,
    }),
  ),
  initialSelected: PropTypes.arrayOf(
    shape({
      name: PropTypes.string,
      value: PropTypes.string.isRequired,
    }),
  ),
  isLoading: PropTypes.bool,
  onChange: PropTypes.func,
  reset: PropTypes.shape({
    value: PropTypes.bool.isRequired,
    setValue: PropTypes.func.isRequired,
  }),
  icon: PropTypes.element,
};

export default Multiselect;
